import { supabase } from '@/lib/supabase';
import type { Currency } from '@sharecost/shared';
import { FALLBACK_CURRENCIES_DATA } from '@sharecost/shared';

interface ExchangeRateEntry {
  from_currency: string;
  rate: number;
  rate_date: string;
}

let cachedRates: Map<string, number> | null = null;
let cacheTimestamp = 0;
const CACHE_TTL = 1000 * 60 * 60; // 1 hour

export async function fetchCurrencies(): Promise<Currency[]> {
  const { data, error } = await supabase
    .from('currencies')
    .select('code, symbol, minor_units')
    .order('code');

  if (error) {
    // Fallback to static data
    return FALLBACK_CURRENCIES_DATA.map(c => ({
      code: c.code,
      symbol: c.symbol,
      minor_units: c.minor_units,
    }));
  }

  return data as Currency[];
}

export async function fetchExchangeRates(): Promise<Map<string, number>> {
  if (cachedRates && Date.now() - cacheTimestamp < CACHE_TTL) {
    return cachedRates;
  }

  const { data, error } = await supabase.rpc('get_latest_exchange_rates');

  if (error) {
    // Fallback to static rates
    const fallbackMap = new Map<string, number>();
    for (const c of FALLBACK_CURRENCIES_DATA) {
      fallbackMap.set(c.code, c.rate_to_usd);
    }
    return fallbackMap;
  }

  const rateMap = new Map<string, number>();
  for (const entry of (data as ExchangeRateEntry[])) {
    rateMap.set(entry.from_currency, entry.rate);
  }
  rateMap.set('USD', 1);

  cachedRates = rateMap;
  cacheTimestamp = Date.now();

  return rateMap;
}

/**
 * Get exchange rate between two currencies via USD.
 * rate(from → to) = rate_to_usd[from] / rate_to_usd[to]
 */
export async function getExchangeRate(from: string, to: string): Promise<number> {
  if (from === to) return 1;

  const rates = await fetchExchangeRates();
  const fromRate = rates.get(from);
  const toRate = rates.get(to);

  if (!fromRate || !toRate) {
    throw new Error(`Exchange rate not found for ${from} → ${to}`);
  }

  return fromRate / toRate;
}
